'use client';

import { useCallback } from 'react';
import { useTrack } from '@frameio/shared/segment/hooks';
import { AlertDialog, Box, Button2, Text, theme } from '@frameio/vapor';
import { IconArrowUpRightSquareFill } from '@frameio/vapor-icons';
import TEXT from './generated/TEXT/FireflyTranslateConsentDialog';

TEXT.DEFINE({
  /** Dialog title */
  title: 'About Translating Video',
  /** Info text explaining consent requirements */
  infoText:
    'This feature analyzes voices and faces to enable translations. By using it, you confirm all individuals consent. Adobe will not use your content to train AI.',
  /** Label for learn more link */
  learnMore: 'Learn More',
  /** Cancel button label */
  cancel: 'Cancel',
  /** Agree button label */
  iAgree: 'I Agree',
});

type FireflyTranslateConsentDialogProps = {
  isShowing: boolean;
  infoHref?: string;
  onAgree: (consentTimestamp: string) => void;
  onCancel: () => void;
  assetCount?: number;
};

const INFO_TEXT_HREF =
  'https://developer.adobe.com/audio-video-firefly-services/getting_started/usage/';

export const FireflyTranslateConsentDialog = ({
  isShowing,
  onAgree,
  onCancel,
  assetCount = 0,
}: FireflyTranslateConsentDialogProps) => {
  const track = useTrack();

  const handleCancel = useCallback(() => {
    track('generation-consent-declined', {
      action_name: 'Translate Video',
      number_of_assets: assetCount,
    });
    onCancel();
  }, [track, assetCount, onCancel]);

  const handleAgree = useCallback(() => {
    const ts = new Date().toISOString();
    onAgree(ts);
  }, [onAgree]);

  return (
    <AlertDialog
      actions={
        <>
          <Button2 onPress={handleCancel} variant="secondary">
            {TEXT.cancel}
          </Button2>
          <Button2 onPress={handleAgree} variant="primary">
            {TEXT.iAgree}
          </Button2>
        </>
      }
      aria-label={TEXT.title}
      details={
        <Box alignItems="center" display="flex" flexDirection="column" gap={8}>
          <Text
            color="body"
            fontWeight="regular"
            textAlign="center"
            variant="body"
          >
            {TEXT.infoText}
          </Text>
          <Box alignItems="center" display="inline-flex" gap={2}>
            <Text fontWeight="regular">{TEXT.learnMore}</Text>
            <a
              href={INFO_TEXT_HREF}
              rel="noreferrer"
              style={{
                display: 'inline-flex',
                marginLeft: 2,
              }}
              target="_blank"
            >
              <IconArrowUpRightSquareFill
                color={theme.colors.text.tertiary}
                size={16}
              />
            </a>
          </Box>
        </Box>
      }
      isShowing={isShowing}
      stackActionButtons={true}
      title={TEXT.title}
    />
  );
};
